import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, GoldButton } from "@/components/shared";
import { Emblem } from "@/components/Emblem";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", shore: "lincolnshire" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      toast.success("Your account is ready. Welcome to the crossing.");
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 bg-[#F8F1E7] min-h-[70vh]">
      <Container>
        <div className="max-w-md mx-auto bg-[#FFFAF3] border border-[#D6D3CB] p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4"><Emblem size={56} /></div>
            <h1 className="font-serif text-2xl text-[#003366]">Create an account</h1>
            <p className="font-sans text-sm text-[#505A68] mt-1">Then submit a listing for approval.</p>
          </div>

          {error && (
            <div data-testid="signup-error" className="mb-5 bg-[#003366]/5 border border-[#003366]/30 text-[#003366] text-sm font-sans px-4 py-3">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className={labelCls}>Your name</label>
              <input required data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input required type="email" data-testid="signup-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input required type="password" minLength={6} data-testid="signup-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Your shore</label>
              <select data-testid="signup-shore" value={form.shore} onChange={(e) => setForm({ ...form, shore: e.target.value })} className={inputCls}>
                <option value="lincolnshire">Lincolnshire</option>
                <option value="massachusetts">Massachusetts</option>
                <option value="both">Both shores</option>
              </select>
            </div>
            <GoldButton type="submit" disabled={busy} data-testid="signup-submit" className="w-full">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
            </GoldButton>
          </form>

          <p className="text-center font-sans text-sm text-[#505A68] mt-6">
            Already registered? <Link to="/signin" className="text-[#003366] font-semibold link-underline">Sign in</Link>
          </p>
        </div>
      </Container>
    </section>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";
